import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, Mail, MessageSquare, RefreshCw, ArrowLeft, Inbox, MapPinned, Coffee, Settings, FormInput } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Logo } from "../components/Logo";
import { PopUpsManager } from "../components/PopUpsManager";
import { ProductsManager } from "../components/ProductsManager";
import { SiteSettingsManager } from "../components/SiteSettingsManager";
import { FormManager } from "../components/FormManager";

const formatWhen = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
};

export default function Admin() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("popups"); // "popups" | "products" | "submissions" | "settings"

  const [contacts, setContacts] = useState([]);
  const [popups, setPopups] = useState([]);
  const [products, setProducts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [loadingPopups, setLoadingPopups] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [c, p, pr] = await Promise.all([
          api.get("/contacts"),
          api.get("/popups"),
          api.get("/products"),
        ]);
        if (!cancelled) {
          setContacts(c.data);
          setPopups(p.data);
          setProducts(pr.data);
        }
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message || "Failed to load");
      } finally {
        if (!cancelled) {
          setLoadingContacts(false);
          setLoadingPopups(false);
          setLoadingProducts(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const refresh = async () => {
    setLoadingContacts(true);
    setLoadingPopups(true);
    setLoadingProducts(true);
    try {
      const [c, p, pr] = await Promise.all([
        api.get("/contacts"),
        api.get("/popups"),
        api.get("/products"),
      ]);
      setContacts(c.data);
      setPopups(p.data);
      setProducts(pr.data);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Failed to load");
    } finally {
      setLoadingContacts(false);
      setLoadingPopups(false);
      setLoadingProducts(false);
    }
  };

  return (
    <main data-testid="admin-page" className="min-h-screen bg-[#FAF9F6] text-[#2C1E16]">
      <header className="border-b border-[#E8E3DD] sticky top-0 z-30 bg-[#FAF9F6]/90 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 py-4 flex items-center justify-between gap-4">
          <Link to="/" data-testid="admin-back" className="inline-flex items-center gap-2 text-sm text-[#5C4E46] hover:text-[#2C1E16]">
            <ArrowLeft size={16} strokeWidth={1.5} />
            <span className="hidden sm:inline">Back to site</span>
          </Link>
          <Logo testId="admin-logo" />
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-refresh"
              onClick={refresh}
              className="inline-flex items-center gap-2 text-sm bg-white border border-[#E8E3DD] text-[#2C1E16] rounded-full px-3.5 py-2 hover:bg-[#F3EDE4] transition-colors"
              title="Refresh"
            >
              <RefreshCw size={14} strokeWidth={1.6} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="inline-flex items-center gap-2 text-sm bg-[#2C1E16] text-[#FAF9F6] hover:bg-[#3E2D24] rounded-full px-3.5 py-2 transition-colors"
            >
              <LogOut size={14} strokeWidth={1.6} />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 sm:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-[#8D99AE] mb-2">Dashboard</div>
            <h1 className="font-display text-3xl sm:text-4xl">Welcome back{user?.email ? `, ${user.email.split("@")[0]}` : ""}.</h1>
          </div>
        </div>

        <div className="inline-flex bg-white border border-[#E8E3DD] rounded-full p-1 mb-10 flex-wrap" role="tablist">
          <TabButton testId="admin-tab-popups" active={tab === "popups"} onClick={() => setTab("popups")} icon={<MapPinned size={14} strokeWidth={1.6} />}>
            Pop-ups
          </TabButton>
          <TabButton testId="admin-tab-products" active={tab === "products"} onClick={() => setTab("products")} icon={<Coffee size={14} strokeWidth={1.6} />}>
            Products
          </TabButton>
          <TabButton testId="admin-tab-submissions" active={tab === "submissions"} onClick={() => setTab("submissions")} icon={<Inbox size={14} strokeWidth={1.6} />}>
            Submissions
          </TabButton>
          <TabButton testId="admin-tab-form" active={tab === "form"} onClick={() => setTab("form")} icon={<FormInput size={14} strokeWidth={1.6} />}>
            Contact form
          </TabButton>
          <TabButton testId="admin-tab-settings" active={tab === "settings"} onClick={() => setTab("settings")} icon={<Settings size={14} strokeWidth={1.6} />}>
            Site settings
          </TabButton>
        </div>

        {err && (
          <div data-testid="admin-error" className="rounded-2xl border border-[#E8E3DD] bg-white p-6 text-[#B33A3A] mb-6">
            {err}
          </div>
        )}

        {tab === "popups" && (
          <div data-testid="admin-popups-pane">
            {loadingPopups ? (
              <div className="text-[#8D99AE] text-sm">Loading pop-ups…</div>
            ) : (
              <PopUpsManager popups={popups} onChange={setPopups} />
            )}
          </div>
        )}

        {tab === "products" && (
          <div data-testid="admin-products-pane">
            {loadingProducts ? (
              <div className="text-[#8D99AE] text-sm">Loading products…</div>
            ) : (
              <ProductsManager products={products} onChange={setProducts} />
            )}
          </div>
        )}

        {tab === "settings" && (
          <div data-testid="admin-settings-pane">
            <SiteSettingsManager />
          </div>
        )}

        {tab === "form" && (
          <div data-testid="admin-form-pane">
            <FormManager />
          </div>
        )}

        {tab === "submissions" && (
          <div data-testid="admin-submissions-pane">
            <div data-testid="admin-count" className="text-sm text-[#8D99AE] mb-6">
              {loadingContacts ? "Loading…" : `${contacts.length} ${contacts.length === 1 ? "entry" : "entries"}`}
            </div>
            {!loadingContacts && contacts.length === 0 && (
              <div data-testid="admin-empty" className="rounded-3xl border border-[#E8E3DD] bg-white p-10 text-center text-[#5C4E46]">
                No submissions yet. Once someone joins the list, they&rsquo;ll show up here.
              </div>
            )}
            <div className="grid gap-3">
              {contacts.map((c) => (
                <SubmissionCard key={c.id} c={c} />
              ))}
            </div>
          </div>
        )}
      </section>
    </main>
  );
}

const SubmissionCard = ({ c }) => {
  const vals = c.values || {};
  const display = vals.name || vals.full_name || vals.email || "Anonymous";
  const reservedKeys = new Set(["name", "email", "phone", "message"]);
  const extraKeys = Object.keys(vals).filter((k) => !reservedKeys.has(k));
  return (
    <article
      data-testid={`admin-contact-${c.id}`}
      className="rounded-2xl border border-[#E8E3DD] bg-white p-5 sm:p-6 grid sm:grid-cols-12 gap-4"
    >
      <div className="sm:col-span-3">
        <div className="font-display text-lg">{display}</div>
        <div className="text-xs text-[#8D99AE] mt-1">{formatWhen(c.created_at)}</div>
        <div className="flex flex-wrap gap-1 mt-2">
          {c.mailchimp_status && <StatusPill kind="mc" status={c.mailchimp_status} />}
          {c.notification_status && c.notification_status !== "disabled" && (
            <StatusPill kind="notif" status={c.notification_status} />
          )}
        </div>
      </div>
      <div className="sm:col-span-4 flex flex-col gap-1.5 text-sm text-[#2C1E16] min-w-0">
        {vals.email && (
          <a className="inline-flex items-center gap-2 hover:text-[#5C4E46] truncate" href={`mailto:${vals.email}`}>
            <Mail size={14} strokeWidth={1.5} className="shrink-0" />
            {vals.email}
          </a>
        )}
        {vals.phone && (
          <span className="inline-flex items-center gap-2 text-[#5C4E46]">
            <span className="text-[10px] uppercase tracking-[0.16em] text-[#8D99AE]">phone</span>
            {vals.phone}
          </span>
        )}
        {extraKeys.map((k) => (
          <span key={k} className="inline-flex items-center gap-2 text-[#5C4E46]">
            <span className="text-[10px] uppercase tracking-[0.16em] text-[#8D99AE]">{k.replace(/_/g, " ")}</span>
            {vals[k] === true ? "Yes" : vals[k] === false ? "No" : String(vals[k])}
          </span>
        ))}
      </div>
      <div className="sm:col-span-5">
        {vals.message ? (
          <div className="flex gap-2 text-sm text-[#5C4E46]">
            <MessageSquare size={14} strokeWidth={1.5} className="mt-0.5 shrink-0" />
            <p className="leading-relaxed whitespace-pre-wrap">{vals.message}</p>
          </div>
        ) : (
          <span className="text-xs text-[#8D99AE] italic">No message</span>
        )}
      </div>
    </article>
  );
};

const StatusPill = ({ kind, status }) => {
  const palette = {
    synced: "bg-[#D4A373]/20 text-[#7A4E1F]",
    updated: "bg-[#D4A373]/20 text-[#7A4E1F]",
    sent: "bg-[#D4A373]/20 text-[#7A4E1F]",
    skipped: "bg-[#8D99AE]/15 text-[#5C4E46]",
    disabled: "bg-[#8D99AE]/15 text-[#5C4E46]",
    error: "bg-[#B33A3A]/15 text-[#B33A3A]",
  };
  const label = kind === "mc" ? "Mailchimp" : "Email";
  return (
    <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full ${palette[status] || palette.skipped}`}>
      {label}: {status}
    </span>
  );
};

const TabButton = ({ active, onClick, icon, children, testId }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    role="tab"
    aria-selected={active}
    className={`inline-flex items-center gap-2 text-sm px-4 sm:px-5 py-2 rounded-full transition-colors ${
      active ? "bg-[#2C1E16] text-[#FAF9F6]" : "text-[#2C1E16] hover:bg-[#F3EDE4]"
    }`}
  >
    {icon}
    {children}
  </button>
);
